/* eslint-disable @typescript-eslint/no-explicit-any */
import React from "react";
import Grid from "@mui/material/Grid";
import { useDispatch } from "react-redux";
import { useForm, Controller } from "react-hook-form";
import { Box, Button, TextField, MenuItem } from "@mui/material";

import Panel from "../../../../../components/Panel";
import { tl } from "../../../../../components/translate";
import { WardType } from "../../../../../constants/ward";
import { Ward } from "../../../../../interfaces/WardInterface";
import { notificationAdd } from "../../../../../actions/notification";
import { usePatchWard, usePostWard } from "../../../../../hooks/query/useWards";

interface WardCreateEditProps {
  onClose: () => void;
  ward?: Partial<Ward> | null;
  onSuccess: (data?: Ward) => void;
}

// eslint-disable-next-line import/prefer-default-export
export function WardCreateEdit({ onClose, ward, onSuccess }: WardCreateEditProps): React.ReactNode {
  const isEdit = !!ward?.id;
  const dispatch = useDispatch();

  const { mutate: postWardMutation } = usePostWard();
  const { mutate: patchWardMutation } = usePatchWard();

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting }
  } = useForm<Partial<Ward>>({
    defaultValues: {
      name: ward?.name || "",
      shortCode: ward?.shortCode || "",
      type: ward?.type || WardType.GENERAL,
      floor: ward?.floor || "",
      building: ward?.building || ""
    }
  });

  const onSubmit = async (data: Partial<Ward>) => {
    if (!data.name || !data.shortCode || !data.type) {
      dispatch(
        notificationAdd({
          id: Date.now(),
          variant: "error",
          message: "Please fill required fields: ward name, shortCode and type.",
          autoTimeout: true
        })
      );
      return;
    }

    if (isEdit && ward?.id) {
      patchWardMutation(
        { id: ward.id, data },
        {
          onSuccess: () => {
            onClose();
            onSuccess?.();
          }
        }
      );
    } else {
      postWardMutation(data, {
        onSuccess: () => {
          onClose();
          onSuccess?.();
        }
      });
    }
  };

  const footer = (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        width: "100%",
        justifyContent: "flex-end",
        gap: 2
      }}
    >
      <Button onClick={onClose}>{tl("cancel")}</Button>
      <Button type="submit" variant="contained" form="ward-form" disabled={isSubmitting}>
        {isEdit ? tl("update") : tl("create")}
      </Button>
    </Box>
  );

  return (
    <Panel title={isEdit ? "Edit Ward" : "Create Ward"} onClose={onClose} footer={footer}>
      <Box sx={{ display: "flex", flexDirection: "column" }}>
        <form id="ward-form" onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller
                name="name"
                control={control}
                rules={{ required: "Name code is required." }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    required
                    variant="outlined"
                    label="Name"
                    error={!!errors.name}
                    helperText={errors.name?.message}
                    onFocus={(event) => event.target.select()}
                    slotProps={{
                      inputLabel: { shrink: true }
                    }}
                    placeholder="e.g. General"
                  />
                )}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller
                name="shortCode"
                control={control}
                rules={{ required: "Short code is required." }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    variant="outlined"
                    required
                    label="Short Code"
                    error={!!errors.shortCode}
                    helperText={errors.shortCode?.message}
                    onFocus={(event) => event.target.select()}
                    slotProps={{
                      inputLabel: { shrink: true }
                    }}
                    placeholder="e.g. GEN"
                  />
                )}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller
                name="type"
                control={control}
                rules={{ required: "Ward type is required" }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    fullWidth
                    variant="outlined"
                    required
                    label="Ward Type"
                    error={!!errors.type}
                    helperText={errors.type?.message}
                    slotProps={{
                      inputLabel: { shrink: true }
                    }}
                  >
                    {Object.values(WardType).map((type) => (
                      <MenuItem key={type} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller
                name="building"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    variant="outlined"
                    label="Building"
                    error={!!errors.building}
                    helperText={errors.building?.message}
                    onFocus={(event) => event.target.select()}
                    slotProps={{
                      inputLabel: { shrink: true }
                    }}
                    placeholder="e.g. GEN"
                  />
                )}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller
                name="floor"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    variant="outlined"
                    label="Floor"
                    error={!!errors.floor}
                    helperText={errors.floor?.message}
                    onFocus={(event) => event.target.select()}
                    slotProps={{
                      inputLabel: { shrink: true }
                    }}
                    placeholder="e.g. First Floor"
                  />
                )}
              />
            </Grid>
          </Grid>
        </form>
      </Box>
    </Panel>
  );
}
